import { useState, useEffect } from "react";

import { formatDistanceToNow } from "date-fns";
import { History, RotateCcw, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";




interface MemoryHistoryItem {
  id: string;
  content: string;
  version: number;
  created_at: string;
}

interface MemoryHistoryProps {
  memoryId: string;
  currentVersion: number;
  onRestore: (content: string) => void;
}

export const MemoryHistory = ({ memoryId, currentVersion, onRestore }: MemoryHistoryProps) => {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<MemoryHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open, memoryId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("memory_history")
        .select("*")
        .eq("memory_id", memoryId)
        .order("version", { ascending: false });

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Failed to fetch history");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (content: string, version: number) => {
    onRestore(content);
    setOpen(false);
    toast.success(`Restored to version ${version}`);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="gap-2"
      >
        <History className="h-4 w-4" />
        History (v{currentVersion})
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Memory Version History
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No previous versions available
              </div>
            ) : (
              history.map((item) => (
                <Card key={item.id} className="p-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">Version {item.version}</Badge>
                      <span className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(item.content, item.version)}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Restore
                    </Button>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{item.content}</p>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
